"use client";

import { useEffect, useRef } from "react";
import Clarity from "@microsoft/clarity";
import { useAuthStore } from "@/stores/auth-store";
import { useWorkspaceStore } from "@/stores/workspace-store";

/**
 * ClarityProvider — initialises Microsoft Clarity and identifies the logged-in
 * user + active tenant (company / workspace) for session filtering.
 *
 * ── ENV-SEPARATED (staging vs production) ────────────────────────────────────
 * The project id comes ONLY from `NEXT_PUBLIC_CLARITY_PROJECT_ID`, which is
 * inlined at FE BUILD time from each box's own `apps/web/.env`:
 *   - staging  → its own Clarity project
 *   - production → its own Clarity project
 *   - local / a tier that opted out → the var is empty → Clarity never loads.
 * There is deliberately NO hardcoded fallback: a hardcoded id previously made
 * staging AND production report into the SAME project. Presence of the id — not
 * `NODE_ENV` — is the on/off switch (both deploy boxes run `NODE_ENV=production`).
 *
 * ── TENANT IDENTITY ──────────────────────────────────────────────────────────
 *   - identify  → user.id (Clarity hashes it before upload) + a friendly name
 *   - tags      → userEmail, role, companyId, companyName, workspaceId,
 *                 workspaceName, domainId, appEnv
 * The active workspace is read from `useWorkspaceStore` (the LIVE source that the
 * TeamSwitcher writes), NOT the auth-store copy — that copy is only set at
 * login/hydration and would otherwise go stale on a workspace switch. Tags are
 * refreshed on every tenant change so a switch re-tags the running session.
 */
export function ClarityProvider() {
  const initialised = useRef(false);
  const identifiedUserId = useRef<string | null>(null);

  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  // LIVE active tenant — updated on every TeamSwitcher switch. Wave-3 item 4
  // (audit 2026-09-25): the domain comes from here too — the auth store's
  // domainId is never set, so the domainId tag was never sent — and there is
  // no fallback to the auth store's login-time workspaceId (the workspace
  // store is persisted, so it is hydrated before this runs).
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);
  const activeCompanyId = useWorkspaceStore((s) => s.activeCompanyId);
  const domainId = useWorkspaceStore((s) => s.activeDomainId);
  const workspaces = useWorkspaceStore((s) => s.workspaces);

  // Initialise once, in the browser, only when a project id is configured for
  // this environment.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (initialised.current) return;

    const projectId = (process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID ?? "").trim();
    if (!projectId) return;

    Clarity.init(projectId);
    initialised.current = true;

    const appEnv = (process.env.NEXT_PUBLIC_APP_ENV ?? "").trim();
    if (appEnv) Clarity.setTag("appEnv", appEnv);
  }, []);

  // Identify + (re)tag whenever the user or the active tenant changes.
  useEffect(() => {
    if (!initialised.current) return;

    if (!isAuthenticated || !user?.id) {
      identifiedUserId.current = null;
      return;
    }

    // Identify once per user session.
    if (identifiedUserId.current !== user.id) {
      identifiedUserId.current = user.id;
      const friendlyName =
        [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email;
      Clarity.identify(user.id, undefined, undefined, friendlyName);
    }

    // The live active workspace. `activeWorkspaceId` may be the 'all'
    // sentinel (company-level view) — a valid, informative tag value.
    const workspaceId = activeWorkspaceId || "";
    const workspaceName =
      workspaces.find((w) => w.id === activeWorkspaceId)?.name || "";
    const companyId = user.companyId || activeCompanyId || "";

    Clarity.setTag("role", user.role || "unknown");
    if (user.email) Clarity.setTag("userEmail", user.email);
    if (companyId) Clarity.setTag("companyId", companyId);
    if (user.companyName) Clarity.setTag("companyName", user.companyName);
    if (workspaceId) Clarity.setTag("workspaceId", workspaceId);
    if (workspaceName) Clarity.setTag("workspaceName", workspaceName);
    if (domainId) Clarity.setTag("domainId", domainId);

    const appEnv = (process.env.NEXT_PUBLIC_APP_ENV ?? "").trim();
    if (appEnv) Clarity.setTag("appEnv", appEnv);
  }, [
    isAuthenticated,
    user,
    domainId,
    activeWorkspaceId,
    activeCompanyId,
    workspaces,
  ]);

  return null;
}
